﻿
import objectFitImages from 'object-fit-images';
import { sitecore, MediaQuery } from '../';

export class LazyLoad {
    constructor (...opts) { 
        this._refs = {
            elements: Array.from(document.querySelectorAll('.lazy, [data-background], [data-background-xs], [data-background-sm], [data-background-md], [data-background-lg], [data-background-xl], [data-background-xxl]'))
        };
         
        this._props = {
            observer: null,
            IE11: !!window.MSInputMethodContext && !!document.documentMode,
            pageEditor: sitecore.isInPageEditor
        };

        if(this._props.observer != null) {
            this._props.observer.disconnect();
        }

        if(this._props.pageEditor) { 
            return false;
        }

        if ("IntersectionObserver" in window) {
            const 
                onIntersection = (entries, observer) => {
                    entries.forEach(x => {
                        if (x.isIntersecting) {
                            this._onIntersection(x.target, (el) => {
                                observer.unobserve(el);
                            });
                        }
                    });
                },
                oIntersectionConfig = { 
                    rootMargin: '300px 0px'
                };

            this._props.observer = new IntersectionObserver(onIntersection, oIntersectionConfig);
            this._refs.elements.forEach(x => this._props.observer.observe(x));
        } else {
            this._refs.elements.forEach(x => {
                this._onIntersection(x, null);
            });

            // Fallback
            if(this._props.IE11) {
                objectFitImages();
            }
        }
    } 

    /**
     * Create and append elements to watched reference
     * @param {String} query - Selector
     * @param {HTMLElement} base - query base (document)
     */
    add (query = '', base = document) {
        if(!query.length) return false;

        const arrElements = Array.from(base.querySelectorAll(query));

        if(arrElements.length) {
            this._refs.elements.push(arrElements);
        }
    }

    /**
     * Handler for executing load-procedure for an item
     * @param {HTMLElement} el - Watched element
     * @param {Function} callback - Function to execute once load has completed
     */
    _onIntersection (el = HTMLElement, callback = Function) {
        /**
         * Replace source with data attribute
         * Remove used data attribute
         * @param {element} el 
         * @param {string} dataSource 
         * @param {string} source 
         */
        const addSource = (el, dataSource, source) => {
            if (typeof el.dataset[dataSource] != 'undefined') {
                el[source] = el.dataset[dataSource];
                el.removeAttribute(`data-${dataSource}`);
            }
        }
        switch(el.nodeName) {
            case "OBJECT":
                const showImage = () => {
                    el.classList.remove('lazy');
                    el.classList.add('is-rendered');
                }
                const displayFallbackImage = () => {
                    const fallback = el.querySelector('img');
                    addSource(fallback, 'src', 'src');
                    showImage();
                } 
                
                addSource(el, 'src', 'data');
                el.addEventListener('load', showImage, false);
                el.addEventListener('error', displayFallbackImage, false);
            break; 
            case "PICTURE":
                let
                    arrChildren = el.children,
                    intLoaded = 0;

                for (let i = 0; Object.keys(arrChildren).length > i; i++) {
                    const x = arrChildren[i];
                    if (typeof x.dataset === 'undefined') continue;

                    if (typeof x.dataset.srcset != 'undefined') {
                        x.srcset = x.dataset.srcset;
                        x.removeAttribute('data-srcset');
                    }

                    if (typeof x.dataset.src != 'undefined') {
                        x.src = x.dataset.src;
                        x.removeAttribute('data-src');
                    }

                    if(x) {
                        x.addEventListener('load', () => {
                            x.classList.remove('lazy');
                        });
                    }

                    intLoaded++;
                }

                if (intLoaded > 0) {
                    if (el.classList.contains('lazy')) {
                        el.classList.remove('lazy');
                    }

                    el.classList.add('is-rendered');
                    if(typeof callback === 'function') {
                        callback(el);
                    }
                }
            break;
            case "IMG":
                if (typeof el.dataset.srcset != 'undefined') {
                    el.srcset = el.dataset.srcset;
                    el.removeAttribute('data-srcset');
                }

                if (typeof el.dataset.src != 'undefined') {
                    el.src = el.dataset.src;
                    el.removeAttribute('data-src');
                }

                if(el) {
                    el.addEventListener('load', () => {
                        el.classList.remove('lazy');
    
                        const oParentEl = el.parentElement;
                        if (oParentEl.classList.contains('lazy')) {
                            el.parentElement.classList.remove('lazy');
                            el.parentElement.classList.add('is-rendered');
                        }
                    });
                }
            break;
            case "DIV": 
                const fn = (callback) => {
                    let                     
                        props = {},
                        strMediaItem = typeof el.dataset.background != 'undefined' ? el.dataset.background : null;
            
                    // Populate props
                    for(const i in el.dataset) {
                        if(i.indexOf('background') > -1) {
                            const key = i.replace('background','').toLowerCase();
                            if(key != '') props[key] = el.dataset[i];
                        }
                    }  

                    const availablePointers = Object.keys(props).sort().reverse();
                    props = this._sort(props);
                    
                    availablePointers.forEach(x => {
                        if(MediaQuery.is(x) || availablePointers.length === 1) {
                            strMediaItem = props[x];
                        }
                    });
                
                    if(strMediaItem != null) {
                        el.style.backgroundImage = "url("+ strMediaItem +")";
                    }

                    if(typeof callback == 'function') {
                        callback(el);
                    }
                };

                // OnIntersection
                fn(callback);

                // Event: MQ
                window.addEventListener('change.mq', fn.bind(this));
            break;
        }
    }

    _sort (obj) {
        return Object.keys(obj).sort().reverse().reduce(function (result, key) {
            result[key] = obj[key];
            return result;
        }, {});
    }
}